/**
 * Pages (sidebar tree): any card, note or task can become a page — it leaves Home and
 * lives here, with subpages under it. On desktop the tree is a drop target:
 * drop on "Pages" for a top-level page, on a page's top/bottom edge to place it before/
 * after, or on its middle to make a subpage. Pages can be dragged within the tree, or
 * onto Home to put them back.
 */
import { useState, useSyncExternalStore } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { childPages, displayTitle, topPages } from '@terrazzo/core'
import { useDerived, useItem } from '../store/hooks'
import { store } from '../store/store'
import { itemPath, openSheet, useLocation } from '../lib/router'
import { useTreeDnd } from '../dnd/TreeDnd'
import { scaled } from '../lib/scale'
import { IconChevronRight, IconPage, IconPlus } from './Icons'
import { ItemIcon } from './ItemIcon'

// Expanded pages, remembered per device.
const KEY = 'tz.pages.open'
let expanded: Set<string> = (() => {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(KEY) ?? '[]'))
  } catch {
    return new Set<string>()
  }
})()
const subs = new Set<() => void>()
function toggle(id: string) {
  expanded = new Set(expanded)
  if (expanded.has(id)) expanded.delete(id)
  else expanded.add(id)
  try {
    localStorage.setItem(KEY, JSON.stringify([...expanded]))
  } catch {
    // private mode: keep it in memory
  }
  for (const f of subs) f()
}
function useExpanded(): Set<string> {
  return useSyncExternalStore(
    (cb) => (subs.add(cb), () => subs.delete(cb)),
    () => expanded,
  )
}

/**
 * Drag a page (to rearrange it: before/after another page, or into one as a subpage) and drop
 * pages, cards, notes or rows on it. Shared by the sidebar tree and the Pages screen; `scope`
 * keeps their ids apart when both show the same page (desktop).
 */
export function usePageDnd(id: string, scope: string, enabled: boolean) {
  const suffix = scope ? `@${scope}` : ''
  const zone = `zone:page:${id}${suffix}`
  const drag = useDraggable({ id: `page:${id}${suffix}`, data: { type: 'page', id }, disabled: !enabled })
  const drop = useDroppable({ id: zone, data: { type: 'zone' }, disabled: !enabled })
  const st = useTreeDnd()?.state
  return {
    ref: (el: HTMLElement | null) => {
      drag.setNodeRef(el)
      drop.setNodeRef(el)
    },
    handlers: enabled ? { ...drag.listeners, ...drag.attributes } : {},
    /** Where something dragged over this page would go. */
    hint: enabled && st?.overRoot === zone && st.activeId !== id ? st.pageDrop : null,
    dragging: drag.isDragging,
  }
}

/** The line above/below a page where a dragged item would land (inside = a ring on the row). */
export function PageDropLine({ hint }: { hint: string | null | undefined }) {
  if (hint === 'before') return <span className="pointer-events-none absolute inset-x-1 -top-px h-0.5 rounded bg-fg" />
  if (hint === 'after') return <span className="pointer-events-none absolute inset-x-1 -bottom-px h-0.5 rounded bg-fg" />
  return null
}

/** New page: the Add pop-up, on Page (inside `parentId` when given). */
export function newPage(parentId: string | null = null) {
  openSheet('add', { mode: 'page', ...(parentId ? { parent: parentId } : {}) })
}

/** Show a page's subpages in the sidebar (once one is added inside it). */
export function expandPage(id: string) {
  if (!expanded.has(id)) toggle(id)
}

export function PagesTree({ onOpen, dnd = false }: { onOpen: (path: string) => void; dnd?: boolean }) {
  const pages = useDerived(() => topPages(store.idx), [])
  const drag = useTreeDnd()
  const { setNodeRef, isOver } = useDroppable({ id: 'zone:pages', data: { type: 'zone' }, disabled: !dnd })
  const dragging = dnd && !!drag?.state.activeId && drag.state.kind !== 'tab'
  return (
    <div>
      <div ref={setNodeRef} className={`flex h-8 items-center rounded-lg pr-1 pl-2 ${isOver ? 'bg-surface-3' : ''}`}>
        <button type="button" onClick={() => onOpen('/pages')} className="flex-1 text-left text-xs text-faint hover:text-fg">
          Pages
        </button>
        <button type="button" onClick={() => newPage()} className="flex h-6 w-6 items-center justify-center rounded-md text-faint hover:bg-surface-2 hover:text-fg" aria-label="New page">
          <IconPlus size={14} />
        </button>
      </div>
      {pages.map((id) => (
        <PageNode key={id} id={id} depth={0} onOpen={onOpen} dnd={dnd} />
      ))}
      {!pages.length && (
        <p className={`rounded-lg px-2 py-2 text-xs leading-snug ${dragging ? 'border border-dashed border-line-2 text-muted' : 'text-faint'}`}>
          {dnd ? 'Drag a card or note here to make it a page.' : 'Make any card or note a page from its ⋯ menu.'}
        </p>
      )}
    </div>
  )
}

function PageNode({ id, depth, onOpen, dnd }: { id: string; depth: number; onOpen: (path: string) => void; dnd: boolean }) {
  const it = useItem(id)
  const kids = useDerived(() => childPages(store.idx, id), [id])
  const open = useExpanded().has(id)
  const { route } = useLocation()
  const active = route.name === 'item' && route.id === id
  const { ref, handlers, hint, dragging } = usePageDnd(id, '', dnd)
  const [hover, setHover] = useState(false)
  if (!it) return null
  const title = displayTitle(it.title, (x) => store.get(x)?.title) || 'Untitled'
  return (
    <>
      <div
        ref={ref}
        {...handlers}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        className={`relative flex h-8 items-center gap-1 rounded-lg pr-1 text-sm ${active ? 'bg-surface-3 font-medium text-fg' : 'text-muted hover:bg-surface-2 hover:text-fg'} ${
          hint === 'inside' ? 'ring-2 ring-fg/60' : ''
        } ${dragging ? 'opacity-40' : ''}`}
        style={{ paddingLeft: scaled(4 + depth * 14) }}
        data-page={id}
      >
        <PageDropLine hint={hint} />
        <button
          type="button"
          onClick={() => toggle(id)}
          className={`flex h-6 w-5 flex-none items-center justify-center text-faint hover:text-fg ${kids.length ? '' : 'invisible'}`}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          aria-expanded={open}
        >
          <IconChevronRight size={13} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
        <ItemIcon icon={it.icon} size={14} fallback={<IconPage size={15} className="flex-none text-faint" />} />
        <button type="button" onClick={() => onOpen(itemPath(id))} className="min-w-0 flex-1 truncate py-1 text-left">
          {title}
        </button>
        {(hover || active) && (
          <button
            type="button"
            onClick={() => newPage(id)}
            className="flex h-6 w-6 flex-none items-center justify-center rounded-md text-faint hover:bg-surface-3 hover:text-fg"
            aria-label={`New page inside ${title}`}
          >
            <IconPlus size={13} />
          </button>
        )}
      </div>
      {open && kids.map((k) => <PageNode key={k} id={k} depth={depth + 1} onOpen={onOpen} dnd={dnd} />)}
    </>
  )
}
